import axios from "axios";
import { toast } from "react-toastify";

const API_BASE_URL = (process.env.REACT_APP_BASEURL || "").trim().replace(/\/$/, "");
const PUBLIC_ENDPOINTS = new Set(["/user/login", "/user/reset"]);
const AUTH_STORAGE_KEYS = [
  "token",
  "tokenExpiresAt",
  "jwt_token",
  "auth_user",
  "userId",
  "userName",
  "userRole",
  "userbusinessId",
  "userbusiness",
  "parentBusinessId",
  "phone",
  "email",
];

let isRedirectingToLogin = false;
let isConfigured = false;

const resolveUrl = (url, baseUrl) => {
  try {
    return new URL(url, baseUrl || window.location.origin);
  } catch {
    return null;
  }
};

const getApiBase = () => resolveUrl(API_BASE_URL);

const isBackendRequest = (url, baseUrl) => {
  const resolvedUrl = resolveUrl(url, baseUrl);
  const apiBase = getApiBase();

  if (!resolvedUrl || !apiBase) return false;

  const basePath = apiBase.pathname.replace(/\/$/, "");
  return (
    resolvedUrl.origin === apiBase.origin &&
    (!basePath ||
      resolvedUrl.pathname === basePath ||
      resolvedUrl.pathname.startsWith(`${basePath}/`))
  );
};

const isPublicRequest = (url, method = "GET", baseUrl) => {
  if (String(method).toUpperCase() !== "POST") return false;

  const resolvedUrl = resolveUrl(url, baseUrl);
  const apiBase = getApiBase();
  if (!resolvedUrl || !apiBase) return false;

  const basePath = apiBase.pathname.replace(/\/$/, "");
  const endpointPath = resolvedUrl.pathname
    .slice(basePath.length)
    .replace(/\/$/, "")
    .toLowerCase();

  return PUBLIC_ENDPOINTS.has(endpointPath);
};

export const clearAuthentication = () => {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const handleApiResponseStatus = (status) => {
  if (status === 401) {
    clearAuthentication();

    if (
      !isRedirectingToLogin &&
      window.location.pathname !== "/sessions/signin"
    ) {
      isRedirectingToLogin = true;
      sessionStorage.setItem("authRedirectReason", "session-expired");
      window.location.replace("/sessions/signin");
    }
  }

  if (status === 403) {
    toast.error("You don't have access to this record.", {
      toastId: "api-forbidden",
    });
  }
};

const configureAxios = () => {
  axios.interceptors.request.use((config) => {
    if (
      isBackendRequest(config.url, config.baseURL) &&
      !isPublicRequest(config.url, config.method, config.baseURL)
    ) {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const config = error.config || {};
      if (
        isBackendRequest(config.url, config.baseURL) &&
        !isPublicRequest(config.url, config.method, config.baseURL)
      ) {
        handleApiResponseStatus(error.response?.status);
      }

      return Promise.reject(error);
    }
  );
};

const configureFetch = () => {
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const request = input instanceof Request ? input : null;
    const url = request?.url || input;
    const method = init.method || request?.method || "GET";
    const isProtectedBackendRequest =
      isBackendRequest(url) && !isPublicRequest(url, method);

    let nextInput = input;
    let nextInit = init;

    if (isProtectedBackendRequest) {
      const token = localStorage.getItem("token");
      if (token) {
        const headers = new Headers(request?.headers || undefined);
        new Headers(init.headers || undefined).forEach((value, key) => {
          headers.set(key, value);
        });
        headers.set("Authorization", `Bearer ${token}`);

        if (request) {
          nextInput = new Request(request, { ...init, headers });
          nextInit = undefined;
        } else {
          nextInit = { ...init, headers };
        }
      }
    }

    const response = await nativeFetch(nextInput, nextInit);
    if (isProtectedBackendRequest) {
      handleApiResponseStatus(response.status);
    }

    return response;
  };
};

export const configureApiClient = () => {
  if (isConfigured) return;
  isConfigured = true;
  configureAxios();
  configureFetch();
};

